import React from 'react';
import { useIntl } from 'react-intl';
import { FormGroup } from '@wheely/ui-kit';

import { UserInputError } from '../UserInputError';
import { ISelectInputProps, SelectInput } from '../../components/SelectInput';
import { isSubList } from '../../components/ListInput';
import s from '../styles.module.css';

type SelectFieldItem<V extends string = string> = {
  value: V;
  title: string;
};

interface IProps<N extends string, V extends string> extends ISelectInputProps<N, V> {
  error?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  inputRef?: React.ForwardedRef<HTMLInputElement>;
}

const SelectField = <N extends string = string, V extends string = string>(props: IProps<N, V>) => {
  const {
    name,
    items,
    value,
    isDisabled,
    onChange,
    error,
    title,
    description,
    inputRef,
    placeholder,
    isClearable,
  } = props;
  const intl = useIntl();

  const hasNoItems = items.length === 0;
  const hasOnlyOneItem = items.length === 1 && !isSubList(items[0]) && items[0].value === value;

  return (
    <div data-test-id="form-select-field">
      <FormGroup
        title={title}
        error={error && <UserInputError>{error}</UserInputError>}
        description={!error && <div data-test-id={`form-select-field__note`}>{description}</div>}
        formGroupProps={{ className: s.formGroup }}
      >
        <SelectInput<N, V>
          ref={inputRef}
          value={value}
          isDisabled={isDisabled || hasNoItems || hasOnlyOneItem}
          onChange={onChange}
          placeholder={
            placeholder ??
            intl.formatMessage({
              defaultMessage: 'Select item',
              id: 'b2Wal/',
              description: 'Select default placeholder',
            })
          }
          items={items}
          name={name}
          hasError={!!error}
          isClearable={isClearable}
        />
      </FormGroup>
    </div>
  );
};

export type { SelectFieldItem };
export { SelectField };
